import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  BarChart3,
  CheckCircle2,
  Circle,
  Clock,
  Database,
  Gauge,
  LayoutDashboard,
  Lightbulb,
  Loader2,
  MessageCircle,
  Settings,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import AgentPageShell from '../../components/agent/AgentPageShell';
import { ResolutionBadge } from '../../components/agent/StatusBadge';
import { fetchAgentDashboard } from '../../lib/agentApi';
import { useAgents } from '../../hooks/useAgents';

const QUICK_ACTIONS = [
  {
    title: 'Configure Knowledge Sources',
    description: 'Choose the spaces and documents this agent may read.',
    icon: Database,
    to: '/dashboard/agent/knowledge-sources',
    tone: 'text-blue-500 bg-blue-500/10',
  },
  {
    title: 'Edit Prompt & Persona',
    description: 'Shape its identity, tone and escalation thresholds.',
    icon: Settings,
    to: '/dashboard/agent/prompt-persona',
    tone: 'text-purple-500 bg-purple-500/10',
  },
  {
    title: 'Review Knowledge Gaps',
    description: 'See what users asked that your documentation does not answer.',
    icon: Lightbulb,
    to: '/dashboard/agent/knowledge-gaps',
    tone: 'text-amber-500 bg-amber-500/10',
    highlight: true,
  },
];

const AgentDashboardPage = () => {
  const navigate = useNavigate();
  const { selectAgent } = useAgents();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = useCallback(
    async (orgId, agentId) => {
      setLoading(true);
      try {
        setData(await fetchAgentDashboard(orgId, agentId, days));
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load the dashboard');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [days]
  );

  return (
    <AgentPageShell
      title="AI Agent Dashboard"
      description="Health, usage and the setup checklist for this agent"
      icon={LayoutDashboard}
      breadcrumb={['Dashboard']}
      actions={
        <Button asChild>
          <Link to="/dashboard/agent/prompt-persona">
            <Settings className="w-4 h-4 mr-2" />
            Configure Agent
          </Link>
        </Button>
      }
    >
      {({ agent, orgId }) => (
        <DashboardBody
          agent={agent}
          orgId={orgId}
          data={data}
          loading={loading}
          days={days}
          setDays={setDays}
          load={load}
          onOpenSession={(sessionId) => {
            selectAgent(agent.id);
            navigate(`/dashboard/agent/chat-logs/${sessionId}`);
          }}
        />
      )}
    </AgentPageShell>
  );
};

const DashboardBody = ({ agent, orgId, data, loading, days, setDays, load, onOpenSession }) => {
  useEffect(() => {
    if (agent?.id && orgId) load(orgId, agent.id);
  }, [agent?.id, orgId, load]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading dashboard…
      </div>
    );
  }
  if (!data) return null;

  const { health, usage, recent_sessions: recent, checklist, gaps } = data;

  return (
    <div className="space-y-8">
      {/* Health strip */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <HealthCard
          title="Agent Status"
          value={health.status === 'online' ? 'Live' : 'Draft'}
          sub={health.status === 'online' ? `Version ${agent.published_version}` : 'Not published yet'}
          icon={<Activity className="w-4 h-4" />}
        >
          <div className="flex items-center gap-1.5 text-xs">
            <span className="relative flex h-1.5 w-1.5">
              {health.status === 'online' && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              )}
              <span
                className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                  health.status === 'online' ? 'bg-emerald-500' : 'bg-slate-400'
                }`}
              />
            </span>
            <span className="text-muted-foreground">
              {health.indexed_chunks.toLocaleString()} indexed chunks
            </span>
          </div>
        </HealthCard>

        <HealthCard
          title="Total Conversations"
          value={health.total_sessions.toLocaleString()}
          sub={`${health.escalations} escalated`}
          icon={<MessageCircle className="w-4 h-4" />}
        />

        <HealthCard
          title="Avg. Confidence"
          value={`${health.avg_confidence}%`}
          icon={<Gauge className="w-4 h-4" />}
        >
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${health.avg_confidence}%` }}
            />
          </div>
        </HealthCard>

        <HealthCard
          title="Resolution Rate"
          value={`${health.resolution_rate}%`}
          icon={<BarChart3 className="w-4 h-4" />}
        >
          <span
            className={`text-xs font-medium ${
              health.resolution_rate >= 80
                ? 'text-emerald-500'
                : health.resolution_rate >= 50
                ? 'text-amber-500'
                : 'text-muted-foreground'
            }`}
          >
            {health.resolution_rate >= 80
              ? 'High performance'
              : health.resolution_rate >= 50
              ? 'Room to improve'
              : 'Needs attention'}
          </span>
        </HealthCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Usage chart */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold">Usage Overview</h3>
                <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                  <TabsList className="h-8">
                    <TabsTrigger value="7" className="text-xs">7d</TabsTrigger>
                    <TabsTrigger value="30" className="text-xs">30d</TabsTrigger>
                    <TabsTrigger value="90" className="text-xs">90d</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={usage} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="agentVoice" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="agentText" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(v) => (v || '').slice(5)}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        borderColor: 'hsl(var(--border))',
                        borderRadius: '8px',
                        color: 'hsl(var(--foreground))',
                      }}
                    />
                    <Legend verticalAlign="top" align="right" height={30} iconType="circle" />
                    <Area
                      type="monotone"
                      dataKey="voice"
                      name="Voice"
                      stroke="hsl(var(--chart-1))"
                      fill="url(#agentVoice)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="text"
                      name="Text"
                      stroke="hsl(var(--chart-2))"
                      fill="url(#agentText)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Quick actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {QUICK_ACTIONS.map((action) => (
              <Card
                key={action.title}
                className={`hover:border-primary/40 transition-colors ${
                  action.highlight ? 'ring-1 ring-amber-500/20' : ''
                }`}
              >
                <CardContent className="p-5 flex flex-col h-full">
                  <div className={`mb-4 w-10 h-10 rounded-lg flex items-center justify-center ${action.tone}`}>
                    <action.icon className="w-5 h-5" />
                  </div>
                  <h4 className="font-semibold mb-2">{action.title}</h4>
                  <p className="text-sm text-muted-foreground flex-1 mb-5">{action.description}</p>
                  <Button variant="outline" className="w-full" asChild>
                    <Link to={action.to}>Open</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Recent conversations */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Recent Conversations</h3>
              <Link
                to="/dashboard/agent/chat-logs"
                className="text-sm font-medium text-primary hover:underline"
              >
                View all
              </Link>
            </div>
            <Card className="overflow-hidden">
              {recent.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  No conversations yet. Try the agent from the Prompt &amp; Persona preview.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>First message</TableHead>
                      <TableHead>Channel</TableHead>
                      <TableHead>Confidence</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">When</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent.map((session) => (
                      <TableRow
                        key={session.id}
                        className="cursor-pointer"
                        onClick={() => onOpenSession(session.id)}
                      >
                        <TableCell className="max-w-[280px]">
                          <span
                            className="text-sm truncate block"
                            dir={session.language === 'ar' ? 'rtl' : 'ltr'}
                          >
                            {session.first_message || '—'}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs capitalize">{session.channel}</TableCell>
                        <TableCell className="text-xs font-mono">
                          {session.ai_confidence == null ? '—' : `${session.ai_confidence}%`}
                        </TableCell>
                        <TableCell>
                          <ResolutionBadge state={session.resolution_state} />
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {formatWhen(session.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </div>
        </div>

        {/* Right rail */}
        <div className="space-y-8">
          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Getting Started</h3>
                <div className="relative h-10 w-10">
                  <svg className="h-full w-full" viewBox="0 0 36 36">
                    <circle className="stroke-muted" strokeWidth="3" fill="transparent" r="16" cx="18" cy="18" />
                    <circle
                      className="stroke-primary transition-all duration-500"
                      strokeWidth="3"
                      strokeDasharray={`${checklist.progress}, 100`}
                      strokeLinecap="round"
                      fill="transparent"
                      r="16"
                      cx="18"
                      cy="18"
                      transform="rotate(-90 18 18)"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] font-bold">{checklist.progress}%</span>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {checklist.steps.map((step) => (
                  <div key={step.key} className="flex items-start gap-3">
                    {step.completed ? (
                      <CheckCircle2 className="mt-0.5 w-5 h-5 text-emerald-500 shrink-0" />
                    ) : (
                      <Circle className="mt-0.5 w-5 h-5 text-muted-foreground shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium ${
                          step.completed ? '' : 'text-muted-foreground'
                        }`}
                      >
                        {step.title}
                      </p>
                      <p className="text-xs text-muted-foreground">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {gaps.total > 0 && (
            <Card className="border-amber-500/20 bg-amber-500/5">
              <CardContent className="p-5 flex gap-4">
                <div className="bg-amber-500/20 p-2 rounded-lg h-fit shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="space-y-3 min-w-0">
                  <div>
                    <h4 className="font-semibold text-amber-900 dark:text-amber-400">
                      Knowledge gaps detected
                    </h4>
                    <p className="text-sm text-amber-800/80 dark:text-amber-500/80 mt-1">
                      {gaps.total} topic{gaps.total === 1 ? '' : 's'} scored below this agent's
                      confidence threshold{gaps.high ? `, ${gaps.high} high priority` : ''}.
                    </p>
                  </div>
                  {gaps.top.length > 0 && (
                    <ul className="space-y-1">
                      {gaps.top.map((gap) => (
                        <li
                          key={gap.key}
                          className="text-xs text-amber-900/80 dark:text-amber-300/80 truncate"
                        >
                          • {gap.topic}
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button size="sm" variant="outline" className="border-amber-500/30" asChild>
                    <Link to="/dashboard/agent/knowledge-gaps">
                      View insights
                      <ArrowUpRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <h4 className="text-sm font-semibold">Knowledge base</h4>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Connected spaces</span>
                <span className="font-medium">{agent.space_ids?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Indexed chunks</span>
                <span className="font-medium">{health.indexed_chunks.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Last synced</span>
                <span className="font-medium">{formatWhen(agent.last_synced_at) || 'Never'}</span>
              </div>
              {agent.has_unpublished_changes && (
                <Badge variant="outline" className="w-full justify-center text-amber-600 border-amber-500/30">
                  Unpublished changes
                </Badge>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const HealthCard = ({ title, value, sub, icon, children }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <h3 className="mt-1 text-2xl font-bold truncate">{value}</h3>
          {sub && <p className="mt-0.5 text-xs text-muted-foreground truncate">{sub}</p>}
        </div>
        {icon && <div className="rounded-full bg-primary/10 p-2 text-primary shrink-0">{icon}</div>}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </CardContent>
  </Card>
);

export function formatWhen(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const diff = Date.now() - then.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const dayCount = Math.floor(hours / 24);
  if (dayCount < 7) return `${dayCount}d ago`;
  return then.toLocaleDateString();
}

export default AgentDashboardPage;
